import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Segmented } from "../../../components/ui";
import { apiError, previewComeback, sendComeback } from "../../../services/comeback";
import MailQuotaNotice from "../emails/MailQuotaNotice";

/**
 * Comeback offer to one or several former subscribers: letter signed by Alain
 * Foka (adapted to who left and who is about to leave) or written message,
 * with Premium days offered right away (people who left only).
 */
const ComebackComposer = ({ items, giftOptions = [0, 7, 14, 30], cooldownDays = 14, onClose, onSent }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("letter");
  const [giftDays, setGiftDays] = useState(0);
  const [lang, setLang] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [sending, setSending] = useState(false);

  const ready = mode === "letter" || (subject.trim() && message.trim());
  const options = { mode, giftDays, lang: lang || undefined, subject, message, force };
  const first = items[0];
  const leavingOnly = items.every((item) => item.state === "cancelling");

  useEffect(() => {
    if (!ready) {
      setPreview(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      previewComeback({ ...options, subscriptionId: first.subscriptionId })
        .then((email) => {
          setPreview(email);
          setPreviewError("");
        })
        .catch((error) => {
          setPreview(null);
          setPreviewError(apiError(error) || t("Preview unavailable"));
        });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, giftDays, lang, subject, message, first.subscriptionId]);

  const send = async () => {
    setSending(true);
    try {
      const result = await sendComeback(items.map((item) => item.subscriptionId), options);
      if (result.sent) toast.success(t("comebackSent", { count: result.sent }));
      if (result.gifted) toast.info(t("comebackGifted", { count: result.gifted, days: giftDays }));
      if (result.skipped?.length) toast.info(`${result.skipped[0].name} : ${result.skipped[0].reason}${result.skipped.length > 1 ? ` (+${result.skipped.length - 1})` : ""}`);
      if (result.failed?.length) toast.error(`${result.failed[0].name} : ${result.failed[0].reason}`);
      onSent(result);
    } catch (error) {
      toast.error(apiError(error) || t("Error updating data"));
      setSending(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered size="xl" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>
          {items.length === 1 ? t("Comeback offer to {{name}}", { name: first.user.name }) : t("comebackOfferTo", { count: items.length })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ledger-composer">
          <div className="ui-form ledger-composer-form">
            <div className="ui-field ui-field-full">
              <Segmented
                label={t("Message type")}
                value={mode}
                onChange={setMode}
                options={[
                  { value: "letter", label: t("Letter from Alain Foka") },
                  { value: "custom", label: t("Written message") },
                ]}
              />
              <p className="ui-hint">
                {mode === "letter"
                  ? t("Personal letter: thanks for the support, latest videos, an invitation to come back (or to keep the subscription for those about to leave). In the user's language.")
                  : t("Your own subject and message. {prenom} is replaced by the first name, {jours} by the days offered. Leave an empty line between paragraphs.")}
              </p>
            </div>
            <div className="ui-field ui-field-full">
              <span className="ui-label">{t("Premium days offered")}</span>
              <Segmented
                label={t("Premium days offered")}
                value={giftDays}
                onChange={setGiftDays}
                options={giftOptions.map((value) => ({ value, label: value ? t("daysCount", { count: value }) : t("None") }))}
              />
              <p className="ui-hint">
                {leavingOnly && giftDays
                  ? t("These people still have their subscription: no days are added, the letter asks them to keep it.")
                  : t("Added to the account right away, before the email (people who left only).")}
              </p>
            </div>
            {mode === "custom" && (
              <>
                <div className="ui-field ui-field-full">
                  <label htmlFor="comeback-subject">{t("Subject")}</label>
                  <input id="comeback-subject" className="ui-input" value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="ui-field ui-field-full">
                  <label htmlFor="comeback-message">{t("Message")}</label>
                  <textarea id="comeback-message" className="ui-input ledger-textarea" rows={8} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} />
                </div>
              </>
            )}
            <div className="ui-field">
              <label htmlFor="comeback-lang">{t("Language")}</label>
              <select id="comeback-lang" className="ui-input" value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="">{t("User's language")}</option>
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            <label className="ledger-check">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              {t("Send even if an offer was sent in the last {{days}} days", { days: cooldownDays })}
            </label>
          </div>

          <div className="ledger-preview">
            <span className="ui-label">
              {t("Preview")}
              {preview?.to ? ` · ${t("to")} ${preview.to}` : ""}
            </span>
            {preview ? (
              <>
                <p className="ledger-preview-subject">{preview.subject}</p>
                <iframe title={t("Preview")} className="ledger-preview-frame" srcDoc={preview.html} sandbox="" />
              </>
            ) : (
              <p className="ui-hint">{previewError || (ready ? t("Loading…") : t("Write the subject and the message to see the email."))}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <div className="mail-quota-slot">
        <MailQuotaNotice recipients={items.length} />
      </div>
      <Modal.Footer>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button type="button" className="ui-btn" disabled={!ready || sending || Boolean(previewError)} onClick={send}>
          <i className="ri-mail-send-line" aria-hidden="true"></i>
          {sending ? t("Sending…") : t("comebackSend", { count: items.length })}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ComebackComposer;
